/**
 * Who may use Howcomet, and for how long.
 *
 * Shared rather than server-only because the parent's page shows the same
 * dates, and two copies of "when does this expire" is how a screen ends up
 * disagreeing with the gate that actually decides.
 *
 * The rules live here as pure functions so they can be tested without a
 * database, a clock, or Apple.
 */
/**
 * The App Store product. One subscription, one price, one family.
 *
 * Shared because three places must agree exactly: the native purchase call, the
 * server that verifies what comes back, and the paywall that names the price. A
 * typo here is not a bug that shows up in testing — StoreKit simply returns no
 * product, and the paywall renders empty with nothing in the log to say why.
 */
export const APPLE_PRODUCT_ID = "com.howcomet.app.monthly";
/** What the paywall says out loud. Apple requires both at the point of sale. */
export const PRICE_COPY = "$7.99 a month";
export const PRICE_DETAIL = "One subscription covers every child on your account, up to five.";
export const TRIAL_DAYS = 7;
/**
 * How long a lapsed subscription keeps working.
 *
 * Apple retries a failed renewal for days — a card that expired over a weekend
 * is the commonest reason a payment fails, and it usually succeeds on the
 * retry. Cutting a child off the same hour would punish a family for their
 * bank's timing. Long enough to cover Apple's billing retry, short enough that
 * it is not a second free month.
 */
export const GRACE_DAYS = 16;
/**
 * Which states the grace window is for.
 *
 * Grace covers a *paid* subscription whose renewal we believe is still coming:
 * either Apple has told us a payment failed and it is retrying (`grace`), or we
 * believe the subscription is live and the renewal simply has not reached us
 * yet (`active`). The second matters more than it looks — a notification we
 * miss because the server was restarting must not cut a family off to the
 * second, so not knowing is treated as "keep working".
 *
 * Everything else is excluded because we know better:
 *
 * - `trialing` never had a payment to fail. Granting grace here quietly turned
 *   a seven-day trial into twenty-three days free for every family that signed
 *   up, which is the whole trial again and then some.
 * - `lapsed` means Apple sent EXPIRED — it is over, and Apple has stopped
 *   retrying.
 * - `cancelled` means somebody chose this, or was refunded. They keep the
 *   period they paid for, and not a day more.
 *
 * A comp granted by `npm run admin entitle` is stored as `active`, so it does
 * pick up the grace window. That is sixteen days on a date an operator chose by
 * hand and can revoke, which is not worth a column to prevent.
 */
function graceApplies(status) {
    return status === "active" || status === "grace";
}
/**
 * The one question every route asks.
 *
 * Deliberately generous at the boundary: `>=` rather than `>`. A gate that is a
 * second too strict locks a child out of the app mid-question over a rounding
 * difference between two clocks.
 *
 * The grace window on top of that is not generosity, though — it is cover for
 * Apple's billing retry, and it is granted only to the states in
 * `graceApplies`. Handing it to everyone meant a trial that ran out, or a
 * subscription somebody deliberately cancelled, kept working for another
 * sixteen days.
 */
export function isEntitled(e, now = new Date()) {
    if (!e.entitledUntil)
        return false;
    if (isCurrent(e, now))
        return true;
    if (!graceApplies(e.planStatus))
        return false;
    return e.entitledUntil.getTime() + GRACE_DAYS * 86_400_000 >= now.getTime();
}
/** Inside the paid period, as opposed to inside the grace that follows it. */
export function isCurrent(e, now = new Date()) {
    return Boolean(e.entitledUntil && e.entitledUntil.getTime() >= now.getTime());
}
/** Days left, for a parent's page. Never negative, never a fraction of a day. */
export function daysLeft(e, now = new Date()) {
    if (!e.entitledUntil)
        return 0;
    const ms = e.entitledUntil.getTime() - now.getTime();
    return Math.max(0, Math.ceil(ms / 86_400_000));
}
/** The trial every family starts on, granted at sign-up. */
export function trialUntil(from = new Date()) {
    return new Date(from.getTime() + TRIAL_DAYS * 86_400_000);
}
/**
 * What a parent is told, in their own page.
 *
 * Written for the person who is deciding whether to pay, and honest in both
 * directions — it never says "expired" while the app still works, and never
 * implies the app still works when it does not.
 */
export function entitlementCopy(e, now = new Date()) {
    if (!e.entitledUntil)
        return "Howcomet is asleep. Subscribe to wake it up.";
    const left = daysLeft(e, now);
    if (isCurrent(e, now)) {
        if (e.planStatus === "trialing") {
            return left === 1 ? "Last day of your free trial." : `${left} days left of your free trial.`;
        }
        if (e.planStatus === "cancelled") {
            return `Cancelled. Howcomet keeps working for ${left} more ${left === 1 ? "day" : "days"}.`;
        }
        return left <= 3 ? `Renews in ${left} ${left === 1 ? "day" : "days"}.` : "Subscription active.";
    }
    if (isEntitled(e, now)) {
        /*
          Inside grace. Say what is wrong and what happens, without alarm — and do
          not assert a failed payment unless Apple actually reported one. The other
          way here is a renewal we have not heard about yet, and telling a parent
          their payment was declined when it was not sends them to their bank over
          our own missed notification.
        */
        return e.planStatus === "grace"
            ? "We couldn't take the payment. Howcomet keeps working while your bank and Apple sort it out."
            : "Your subscription hasn't renewed yet. Howcomet keeps working while we check with the App Store.";
    }
    return "Howcomet is asleep. Subscribe to wake it up.";
}
//# sourceMappingURL=entitlement.js.map